/**
 * Calculate School Fit Scores based on available data
 * Returns scores for Academic Match, Cultural Fit, and Career Alignment
 * UPDATED: Dynamic scoring based on tier, competitiveness, and actual alignment
 */

export const calculateSchoolFitScores = (college, studentProfile) => {
  const gc = college.global_colleges || {};
  const tier = (college.category || '').toLowerCase();

  // Helper: Get base score based on tier and competitiveness
  const getBaseScore = (defaultBase) => {
    // Adjust base by tier - reach schools get lower bases, safety schools higher
    if (tier === 'reach') return defaultBase - 10;
    if (tier === 'safety') return defaultBase + 10;
    return defaultBase; // target tier
  };

  // Academic Match Score (0-100)
  const calculateAcademicMatch = () => {
    let score = getBaseScore(72); // Dynamic base: 62 (reach), 72 (target), 82 (safety)

    // SAT score alignment - more granular scoring
    if (studentProfile?.sat_total && gc.sat_middle_50_range) {
      const studentSAT = parseInt(studentProfile.sat_total);
      const [satMin, satMax] = gc.sat_middle_50_range.split('-').map(s => parseInt(s.trim()));
      const satMid = (satMin + satMax) / 2;

      if (studentSAT >= satMax + 50) {
        score += 18; // Well above range
      } else if (studentSAT > satMax) {
        score += 15; // Above range
      } else if (studentSAT >= satMid) {
        score += 12; // Upper half of range
      } else if (studentSAT >= satMin) {
        score += 8; // Lower half of range
      } else if (studentSAT >= satMin - 50) {
        score += 3; // Just below range
      } else {
        score -= 5; // Significantly below
      }
    }

    // GPA alignment - more granular
    if (studentProfile?.gpa_weighted && gc.average_gpa) {
      const studentGPA = parseFloat(studentProfile.gpa_weighted);
      const schoolGPA = parseFloat(gc.average_gpa);
      const diff = studentGPA - schoolGPA;

      if (diff >= 0.3) score += 12;
      else if (diff >= 0.1) score += 9;
      else if (diff >= 0) score += 6;
      else if (diff >= -0.15) score += 3;
      else score -= 3;
    }

    // Major strength alignment
    if (studentProfile?.intended_major && gc.major_strengths) {
      const intendedMajor = studentProfile.intended_major.toLowerCase();
      const majorStrengths = Array.isArray(gc.major_strengths)
        ? gc.major_strengths.join(' ').toLowerCase()
        : (gc.major_strengths || '').toLowerCase();

      if (majorStrengths.includes(intendedMajor)) {
        score += 8;
      } else if (gc.popular_majors && gc.popular_majors.toLowerCase().includes(intendedMajor)) {
        score += 5;
      }
    }

    return Math.min(Math.max(score, 45), 98); // Cap between 45-98
  };

  // Cultural Fit Score (0-100)
  const calculateCulturalFit = () => {
    let score = getBaseScore(76); // Dynamic base: 66 (reach), 76 (target), 86 (safety)

    // Campus setting preference
    if (studentProfile?.preferred_campus_setting && gc.campus_setting) {
      const preferredSetting = (studentProfile.preferred_campus_setting || '').toLowerCase();
      const schoolSetting = (gc.campus_setting || '').toLowerCase();

      if (preferredSetting && schoolSetting.includes(preferredSetting)) {
        score += 12;
      } else if (preferredSetting) {
        score -= 5; // Preference stated but doesn't match
      }
    }

    // Size preference - more granular
    if (studentProfile?.preferred_school_size && gc.student_enrollment) {
      const preferredSize = (studentProfile.preferred_school_size || '').toLowerCase();
      const enrollment = gc.student_enrollment;

      const sizeMatch =
        (preferredSize.includes('small') && enrollment < 5000) ||
        (preferredSize.includes('medium') && enrollment >= 5000 && enrollment <= 15000) ||
        (preferredSize.includes('large') && enrollment > 15000);

      if (sizeMatch) {
        score += 10;
      } else if (preferredSize) {
        score -= 6; // Preference stated but doesn't match
      }
    }

    // Student qualities alignment
    if (studentProfile?.personal_qualities && gc.student_qualities) {
      score += 6;
    }

    // Core values alignment
    if (studentProfile?.values && gc.core_values) {
      score += 6;
    }

    return Math.min(Math.max(score, 50), 95); // Cap between 50-95
  };

  // Career Alignment Score (0-100)
  const calculateCareerAlignment = () => {
    let score = getBaseScore(78); // Dynamic base: 68 (reach), 78 (target), 88 (safety)

    // Career goals alignment
    if (studentProfile?.career_goals && gc.career_outcomes) {
      score += 10;
    } else if (studentProfile?.career_goals) {
      score += 3; // Student has goals, but no data on school outcomes
    }

    // Industry connections
    if (gc.industry_connections) {
      score += 7;
    }

    // Post-grad outcomes
    if (gc.post_grad_outcomes) {
      score += 5;
    }

    // Acceptance rate factor - more selective schools often have better outcomes
    if (gc.acceptance_rate) {
      const acceptRate = parseFloat(gc.acceptance_rate);
      if (acceptRate < 0.10) score += 5; // Highly selective
      else if (acceptRate < 0.25) score += 3;
    }

    return Math.min(Math.max(score, 55), 96); // Cap between 55-96
  };

  return {
    academicMatch: calculateAcademicMatch(),
    culturalFit: calculateCulturalFit(),
    careerAlignment: calculateCareerAlignment()
  };
};

/**
 * Extract insights from college data for display in comparison cards
 */
export const extractCollegeInsights = (college, studentProfile) => {
  const gc = college.global_colleges || {};

  const insights = {
    yourEdge: [],
    mayNotConsidered: [],
    whyFits: ''
  };

  // Your Edge - Extract from student advantages or compute
  if (college.student_advantages) {
    const advantages = Array.isArray(college.student_advantages)
      ? college.student_advantages
      : [college.student_advantages];
    insights.yourEdge = advantages.slice(0, 3);
  } else {
    // Generate edge points from available data
    if (studentProfile?.sat_total && gc.sat_middle_50_range) {
      const [satMin, satMax] = gc.sat_middle_50_range.split('-').map(s => parseInt(s.trim()));
      const studentSAT = parseInt(studentProfile.sat_total);

      if (studentSAT > satMax) {
        insights.yourEdge.push(`<strong>Strong test scores:</strong> Your SAT is above their middle 50% range`);
      }
    }

    if (gc.campus_setting === 'Urban' && studentProfile?.preferred_campus_setting?.includes('urban')) {
      insights.yourEdge.push(`<strong>Setting match:</strong> Urban location aligns with your preferences`);
    }

    if (insights.yourEdge.length === 0) {
      insights.yourEdge = [
        'Strong academic profile alignment',
        'Geographic diversity contribution',
        'Unique extracurricular match'
      ];
    }
  }

  // You May Not Have Considered
  if (gc.special_programs) {
    const programs = Array.isArray(gc.special_programs) ? gc.special_programs : [gc.special_programs];
    insights.mayNotConsidered.push(...programs.slice(0, 2));
  }

  if (gc.research_opportunities && insights.mayNotConsidered.length < 2) {
    const research = Array.isArray(gc.research_opportunities)
      ? gc.research_opportunities[0]
      : gc.research_opportunities;
    if (research) insights.mayNotConsidered.push(research);
  }

  if (gc.study_abroad_programs && insights.mayNotConsidered.length < 2) {
    const abroad = Array.isArray(gc.study_abroad_programs)
      ? gc.study_abroad_programs[0]
      : gc.study_abroad_programs;
    if (abroad) insights.mayNotConsidered.push(`<strong>Study abroad:</strong> ${abroad}`);
  }

  if (insights.mayNotConsidered.length === 0) {
    insights.mayNotConsidered.push('Unique program opportunities and research access for undergraduates');
  }

  // Why This Fits You Better
  if (gc.campus_culture_details) {
    const culture = typeof gc.campus_culture_details === 'string'
      ? gc.campus_culture_details
      : gc.campus_culture_details[Object.keys(gc.campus_culture_details)[0]];
    insights.whyFits = culture || '';
  }

  if (!insights.whyFits && gc.student_enrollment) {
    const size = gc.student_enrollment < 5000 ? 'small' : gc.student_enrollment > 15000 ? 'large' : 'medium';
    insights.whyFits = `${gc.student_enrollment.toLocaleString()}-student ${size} community provides strong academic support and networking opportunities.`;
  }

  if (!insights.whyFits) {
    insights.whyFits = 'This school aligns well with your academic interests and personal goals.';
  }

  return insights;
};
